// SQL null type field extractors

import { CONTRACT_KEYS } from '../constants';

const { PAYMENT_TYPE, HOURLY_RATE, AMOUNT_USD, EXPECTED_DURATION_DAYS } =
  CONTRACT_KEYS;
export const SQL_NULL_TYPES = {
  Int32: (field) => (field.Valid ? field.Int32 : null),
  Int64: (field) => (field.Valid ? field.Int64 : null),
  Float64: (field) => (field.Valid ? field.Float64 : null),
  String: (field) => (field.Valid ? field.String : null),
  Bool: (field) => (field.Valid ? field.Bool : null),
  Time: (field) => (field.Valid ? new Date(field.Time) : null),
};

// Helper to extract values from nullable database fields
export const extractValue = (field) => {
  if (field === null || field === undefined) return null;

  if (typeof field === 'object' && field.Valid !== undefined) {
    for (const [type, extractor] of Object.entries(SQL_NULL_TYPES)) {
      if (Object.hasOwn(field, type)) {
        return extractor(field);
      }
    }
  }

  return field;
};

// =============================================================================
// MILESTONE HELPER FUNCTIONS
// =============================================================================

export const calculateTotalAmount = (milestoneList) =>
  milestoneList.reduce(
    (sum, milestone) => sum + (parseFloat(milestone.amount_usd) || 0),
    0
  );

export const calculateMilestoneDuration = (milestoneList, contract = null) => {
  // Sort milestones by date and calculate total days
  const sortedMilestones = milestoneList
    .filter((m) => m.due_date)
    .map((m) => ({
      ...m,
      parsedDate: new Date(m.due_date),
    }))
    .filter((m) => !isNaN(m.parsedDate.getTime()))
    .sort((a, b) => a.parsedDate - b.parsedDate);

  let totalMilestoneDays = 0;
  if (sortedMilestones.length > 1) {
    const firstDate = sortedMilestones[0].parsedDate;
    const lastDate = sortedMilestones[sortedMilestones.length - 1].parsedDate;
    totalMilestoneDays = Math.ceil(
      (lastDate - firstDate) / (1000 * 60 * 60 * 24)
    );
  } else if (sortedMilestones.length === 1) {
    // For single milestone, calculate duration from contract start date
    const contractStartDate = contract?.start_date
      ? new Date(contract.start_date)
      : new Date();
    const milestoneDate = sortedMilestones[0].parsedDate;

    // Ensure we don't get negative days if milestone is before contract start
    const daysDifference = Math.ceil(
      (milestoneDate - contractStartDate) / (1000 * 60 * 60 * 24)
    );
    totalMilestoneDays = Math.max(0, daysDifference);
  }

  return {
    totalDays: totalMilestoneDays,
    milestoneCount: sortedMilestones.length,
    firstMilestone: sortedMilestones[0],
    lastMilestone: sortedMilestones[sortedMilestones.length - 1],
    sortedMilestones,
  };
};

export const findDuplicateDates = (milestoneList) => {
  const dateMap = new Map();
  const duplicates = [];

  milestoneList.forEach((milestone) => {
    if (milestone.due_date) {
      const count = dateMap.get(milestone.due_date) || 0;
      dateMap.set(milestone.due_date, count + 1);

      if (count === 1) duplicates.push(milestone.due_date);
    }
  });

  return duplicates;
};

export const createSequenceWarning = (
  currentMilestone,
  prevMilestone,
  type,
  formatDateWithMonthYear,
  VALIDATION_TYPES,
  SEVERITY
) => {
  const baseMessage =
    type === 'date'
      ? `<strong>${
          currentMilestone.title
        }</strong> is scheduled for <strong>${formatDateWithMonthYear(
          currentMilestone.due_date
        )}</strong>, which comes before the previous milestone <strong>${
          prevMilestone.title
        }</strong> scheduled for <strong>${formatDateWithMonthYear(
          prevMilestone.due_date
        )}</strong>. Please adjust the dates so each milestone comes after the previous one.`
      : `Milestone order is incorrect. <strong>${currentMilestone.title}</strong> should come after <strong>${prevMilestone.title}</strong> in the project timeline. Please reorder the milestones to follow the proper sequence.`;

  return {
    type:
      type === 'date'
        ? VALIDATION_TYPES.SEQUENCE_ORDER
        : VALIDATION_TYPES.SORT_ORDER,
    milestoneId: currentMilestone.id,
    message: baseMessage,
    severity: SEVERITY.ERROR,
  };
};

export const checkChronologicalOrder = (
  milestoneList,
  formatDateWithMonthYear,
  VALIDATION_TYPES,
  SEVERITY
) => {
  const warnings = [];

  // Filter milestones with valid due dates and sort by sort_order (the intended sequence)
  const sortedByOrder = milestoneList
    .filter((m) => m.due_date)
    .map((m) => ({
      ...m,
      parsedDate: new Date(m.due_date),
      sort_order: m.sort_order || 999, // Default high sort_order for new milestones
    }))
    .filter((m) => !isNaN(m.parsedDate.getTime())) // Only include valid dates
    .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999)); // Sort by intended order

  // Check for invalid date formats first
  milestoneList.forEach((milestone) => {
    if (milestone.due_date) {
      const parsedDate = new Date(milestone.due_date);
      if (isNaN(parsedDate.getTime())) {
        warnings.push({
          type: VALIDATION_TYPES.INVALID_DATE,
          message: `<strong>${milestone.title}</strong> has an invalid due date format: "${milestone.due_date}". Please use a valid date format.`,
          severity: SEVERITY.ERROR,
          milestoneId: milestone.id,
        });
      }
    }
  });

  // Check for chronological order violations based on the intended sequence
  for (let i = 1; i < sortedByOrder.length; i++) {
    const currentMilestone = sortedByOrder[i];
    const prevMilestone = sortedByOrder[i - 1];
    const currentDate = currentMilestone.parsedDate;
    const prevDate = prevMilestone.parsedDate;

    // Check if current milestone date is before or same as previous milestone in the sequence
    if (currentDate <= prevDate) {
      warnings.push(
        createSequenceWarning(
          currentMilestone,
          prevMilestone,
          'date',
          formatDateWithMonthYear,
          VALIDATION_TYPES,
          SEVERITY
        )
      );
    }
  }

  // Additional check: Look for milestones with dates earlier than today
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of day for fair comparison

  sortedByOrder.forEach((milestone) => {
    if (milestone.parsedDate < today) {
      warnings.push({
        type: VALIDATION_TYPES.PAST_DATE,
        message: `<strong>${
          milestone.title
        }</strong> is scheduled for <strong>${formatDateWithMonthYear(
          milestone.due_date
        )}</strong>, which is in the past. Please update the due date to a future date.`,
        severity: SEVERITY.ERROR,
      });
    }
  });

  return warnings;
};

// Format contract data for frontend consumption
export const formatContractData = (contract) => ({
  ...contract,
  created_at: new Date(contract.created_at),
  updated_at: new Date(contract.updated_at),
  start_date: extractValue(contract.start_date),
  end_date: extractValue(contract.end_date),
  expected_duration_days: extractValue(contract.expected_duration_days),
  hourly_rate: extractValue(contract.hourly_rate),
  amount_usd: extractValue(contract.amount_usd) || 0,
  submission_count: extractValue(contract.submission_count) || 0,
  view_count: extractValue(contract.view_count) || 0,
  milestones: contract.milestones || [],
  business_name: contract.business?.name || 'Unknown Business',
  worker_name: contract.worker?.name || null,
});

// Constants
export const SCROLL_THRESHOLD_PX = 300;

// Format submissions for frontend consumption
export const formatSubmissions = (submissions) => {
  if (!Array.isArray(submissions)) return [];

  return submissions.map((submission) => ({
    ...submission,
    created_at: new Date(submission.created_at),
    updated_at: new Date(submission.updated_at),
    submitted_at: extractValue(submission.submitted_at),
    reviewed_at: extractValue(submission.reviewed_at),
    amount_usd: extractValue(submission.amount_usd) || 0,
    description: extractValue(submission.description) || '',
    status: extractValue(submission.status) || 'pending',
    proposal_text: extractValue(submission.proposal_text) || '',
    cover_letter: extractValue(submission.cover_letter) || '',
  }));
};

// Calculate milestone statistics
export const calculateMilestoneStats = (milestones) => {
  if (!Array.isArray(milestones)) {
    return {
      totalAmount: 0,
      paidAmount: 0,
      completionPercentage: 0,
      totalMilestones: 0,
      completedMilestones: 0,
    };
  }

  const totalAmount = milestones.reduce(
    (sum, milestone) => sum + extractValue(milestone.amount_usd),
    0
  );

  const paidAmount = milestones.reduce((sum, milestone) => {
    // Only add amount if milestone is completed
    if (milestone.completed_at) {
      return sum + milestone.amount_usd || 0;
    }
    return sum;
  }, 0);

  const completedMilestones = milestones.filter(
    (milestone) => milestone.completed_at
  ).length;

  const completionPercentage =
    totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;

  return {
    totalAmount,
    paidAmount,
    completionPercentage: Math.round(completionPercentage * 100) / 100,
    totalMilestones: milestones.length,
    completedMilestones,
  };
};

// Create contract update payload
export const createContractUpdatePayload = (contract, changedFields) => {
  const payload = {};
  if (changedFields.length) {
    changedFields.forEach((field) => {
      if ([AMOUNT_USD, EXPECTED_DURATION_DAYS, HOURLY_RATE].includes(field)) {
        payload[field] = Number(contract[field]);
      } else if (field === PAYMENT_TYPE && contract[field] === 'fixed') {
        payload[field] = contract[field];
        payload[HOURLY_RATE] = null;
      } else {
        payload[field] = contract[field];
      }
    });
  }

  return payload;
};
